import { Component, Element, h, Method, Prop, State, Watch } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import {
  HEADLINE_SCHEME_CONFIG,
  HEADLINE_SCHEME_MAP,
  HEADLINE_SIZE_COPY,
  HEADLINE_SIZE_H3
} from "./constants/headline-constants";
import { HeadlineSchemeDefinition, HeadlineSchemeName, HeadlineSize, HeadlineTag } from "./constants/headline-types";
import { HeadlineSchemeConfiguration } from "../../constants/colors";
import pfx from "../../utils/style-class-prefix-helper";
import { useTheRightHtmlTagForHeadline } from "../../utils/headline-helper";
import { HEADLINE_DEFAULT_ALIGNMENT, HeadlineAlignment } from "../../constants/global-constants";
import {
  ContainerBreakpointComponent,
  initContainerBreakpointsDelegate
} from "../../common/container-breakpoint-component";

import { BackgroundColor } from "../background/constants/background-types";
import sanitizeHtml from "sanitize-html";
import { StyledComponent } from "../../decorators/styled-component";
import React from 'react';
import IntrinsicElements = React.JSX.IntrinsicElements;
// import { InitLogging } from "../../decorators/init-logging";


/**
 * @slot info-icon - Holds a single envc-eon-ui-info-icon element
 */
@Component({
  tag: "envc-eon-ui-headline",
  styleUrl: "headline.scss",
  assetsDirs: ["assets-headline"],
  shadow: true
})
export class Headline
  implements ColorableComponent<HeadlineSchemeConfiguration<HeadlineSchemeDefinition>>, ContainerBreakpointComponent {
  // @InitLogging() sentryTransaction;
  schemeConfig: HeadlineSchemeConfiguration<HeadlineSchemeDefinition> = HEADLINE_SCHEME_CONFIG;
  schemeMap = HEADLINE_SCHEME_MAP;

  /**
   * Array that defines component specific container breakpoints.
   */
  containerBreakpoints = [1200, 800, 400];
  private breakpoints = this.containerBreakpoints.sort((a, b) => a - b);
  private smallestBreakpoint = this.breakpoints[0];

  @State() activeScheme: HeadlineSchemeDefinition;
  @State() currentContainerBreakpoint: string = "largest";
  @State() isHidden: boolean = false;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEnvcEonUiHeadlineElement;
  @State() processedText: string;
  @State() seoHeadlineTag: string;
  @State() infoIconComponent: HTMLEnvcEonUiInfoIconElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or envc-eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: HeadlineSchemeName;

  /**
   * The size of the headline.<br>If the property "headlineHtmlTag" is not defined,
   *   this property is used to generate the HTML-Tag for the headline,<br>
   *     e.g.if size="h1" the HTML-Tag "H1" is used for the headline.<br>
   *     **IMPORTANT**: This property **has an** effect on the styles!
   * @example <envc-eon-ui-headline size="h1" text="Better Tomorrow"></envc-eon-ui-headline>
   */
  @Prop() size: HeadlineSize = HEADLINE_SIZE_H3;

  /**
   * Container based size
   */
  @Prop({ reflect: true }) sizeContainerBased: boolean = false;

  /**
   * Changes font-weight to regular.
   */
  @Prop({ reflect: true }) fontWeightRegular: boolean = false;

  /**
   * this property is used for SEO-Optimisation.<br>
   *   If this property is used and fill-out, this property is used for HTML-Tag for headline,<br>
   *     e.g. if headlineHtmlTag="h1" the HTML-Tag "H1" is used for the headline.<br>
   *     **IMPORTANT**: This property **does not** affect the styles!
   * @example <envc-eon-ui-headline size="h5" headlineHtmlTag="h1" text="Better Tomorrow"></envc-eon-ui-headline>
   */
  @Prop() headlineHtmlTag: HeadlineTag;

  /**
   * Alignment of the headline.
   * @example <envc-eon-ui-headline size="h3" alignment="center" text="Better Tomorrow"></envc-eon-ui-headline>
   */
  @Prop() alignment: HeadlineAlignment = HEADLINE_DEFAULT_ALIGNMENT;

  /**
   * Ignore alignment property
   */
  @Prop() ignoreAlignment: boolean = false;

  /**
   * Text content of the headline
   *
   * @example <envc-eon-ui-headline size="h1" text="Better Tomorrow"></envc-eon-ui-headline>
   */
  @Prop() text: string;

  /**
   * short Text content of the headline<br>this text will be used if the container-size is smaller than 400px
   *
   */
  @Prop() shortText: string;

  /**
   * A boolean property that can be used to disable line breaks.
   */
  @Prop() disableLineWrap: boolean = false;

  /**
   * If the headline component has its natural margin defined by the e.on brand design. Omit only if the headline is used in spaces where the margin can be removed because of other css
   * (for example when using a headline in a flexbox layout)
   */
  @Prop() margin = true;

  /**
   * Whether or not the headline text should have a text shadow. Use this if the headline is placed on top of an image and you need more contrast between image and headline.
   */
  @Prop() textShadow = false;

  /**
   * A boolean property that can be used to enable line hyphenation.<br>
   *   Important!: If you have activated hyphenation, note that you must then also set the language, see also the property "language".
   */
  @Prop() hyphenation: boolean = false;

  /**
   * if you enabled hyphenation, it is important that the language for the headline is defined.<br>
   *  There is an automatism that finds out in which language the HTML document exists. If no lang attribute is specified at the parent tags, then the language must be set manually.
   *  <br>Look <a href="https://www.w3schools.com/tags/ref_language_codes.asp" target="_blank">here</a> for a List of available language code.
   */
  @Prop() language: string;

  /**
   * Specifies the label to use for accessibility.
   */
  @Prop({ attribute: "a11y-aria-label", mutable: true }) a11yAriaLabel: string;

  /**
   * Sets the container breakpoint
   * the container breakpoint is used to change the text of the headline if the
   * container size is smaller than the smallest container-breakpoint
   * @param containerBreakpoint
   */
  @Method()
  async setContainerBreakpoint(containerBreakpoint: string) {
    this.currentContainerBreakpoint = containerBreakpoint;
    if (this.currentContainerBreakpoint.toString() === "largest") {
      this.isHidden = false;
    }
  }

  componentWillLoad() {
    this.applyScheme();
    this.handleHeadlineTag();
    this.initContainerBreakpoints();
    this.updateProcessedText(this.text);
    this.infoIconComponent = this.hostElement.querySelector('[slot="info-icon"]');
    // if shortText is set, hide the headline to avoid flickering
    if (this.shortText) this.isHidden = true;
  }

  componentDidLoad() {
    this.setCssVariables();
    this.applyInfoIconScheme();
  }

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  @Watch("scheme")
  applyScheme(): void {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  initContainerBreakpoints(): void {
    initContainerBreakpointsDelegate(this.hostElement, this.containerBreakpoints);
  }

  @Watch("infoIconComponent")
  @Watch("scheme")
  applyInfoIconScheme(): void {
    this.infoIconComponent &&
      this.infoIconComponent.setAttribute("scheme", this.activeScheme.color.replace("eon-", ""));
  }

  /**
   * Executes whenever the text property changes. Replaced the specified linewrap symbol with <br />.
   */
  updateProcessedText(text: string): void {
    let usedText = text;
    const slashN = "\\n";
    if ((this.text || this.shortText) && !this.disableLineWrap) {
      this.processedText = usedText.split(slashN).join("<br />");
    } else {
      this.processedText = usedText;
    }
    // if headline-Text is set, show the headline
    this.isHidden = false;
  }

  @Watch("headlineHtmlTag")
  @Watch("size")
  handleHeadlineTag() {
    this.seoHeadlineTag = useTheRightHtmlTagForHeadline(this.headlineHtmlTag, this.size === "copy" ? "p" : this.size);
  }

  @Watch("text")
  @Watch("shortText")
  @Watch("currentContainerBreakpoint")
  selectTextForHeadline() {
    if (this.shortText && this.currentContainerBreakpoint.toString() === this.smallestBreakpoint.toString()) {
      // 400
      this.updateProcessedText(this.shortText);
    } else {
      this.updateProcessedText(this.text);
    }
  }

  render() {
    //build headline tag
    const TagForHeadline = `${this.seoHeadlineTag.toLowerCase()}` as keyof IntrinsicElements;
    // rename HeadlineTag in TagForHeadline because of using HeadlineTag in "./constants/headline-types" (sonar issue)
    return (
      <TagForHeadline
        lang={this.language}
        class={pfx({
          ["headline"]: true,
          ["is-hidden"]: this.isHidden,
          ["info-icon-display"]: this.infoIconComponent,
          [this.alignment]: this.alignment,
          ["ignore-alignment"]: this.ignoreAlignment,
          ["heading-" + this.size]: true,
          ["no-margin"]: !this.margin,
          ["text-shadow"]: this.textShadow,
          ["hyphenation"]: this.hyphenation,
          ["container-based-size"]: this.sizeContainerBased,
          ["copy"]: this.size === HEADLINE_SIZE_COPY,
          ["font-weight-regular"]: this.fontWeightRegular
        })}
        {...(this.a11yAriaLabel ? { "aria-label": this.a11yAriaLabel } : {})}
      >
        <span innerHTML={sanitizeHtml(this.processedText)}></span>
        {this.infoIconComponent && (
          <div class={pfx("headline-info-icon")}>
            <slot name="info-icon" />
          </div>
        )}
      </TagForHeadline>
    );
  }
}
